import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import { submitRequest, sendCode, verifyCode } from '../../config/previewAccess';
import {
  LandingShell, Narrow, Panel, Eyebrow, H2, Sub, Field, ErrorText,
  PrimaryButton, GhostButton, PrimaryLink, T,
} from './ui';

const Ico = (n: keyof typeof FiIcons) => FiIcons[n] as React.ComponentType<any>;
const FiArrowRight = Ico('FiArrowRight');
const FiArrowLeft = Ico('FiArrowLeft');
const FiCheck = Ico('FiCheck');
const FiClock = Ico('FiClock');
const FiMail = Ico('FiMail');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STEPS = ['About you', 'Verify email', 'Choose apps', 'Use case', 'Review'];
const ORG_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
const INDUSTRIES = ['Property management', 'Pest control', 'Health & safety', 'Healthcare', 'Facilities', 'Other'];

const SignupWizard: React.FC = () => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [orgSize, setOrgSize] = useState('');
  const [industry, setIndustry] = useState('');
  const [apps, setApps] = useState<string[]>([]);
  const [useCase, setUseCase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // email verification
  const [demoCode, setDemoCode] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);

  const onEmail = (v: string) => { setEmail(v); setVerified(false); setDemoCode(null); setCode(''); setVerifyMsg(null); };
  const toggleApp = (id: string) =>
    setApps(prev => (prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]));

  const onSendCode = async () => {
    setSending(true); setVerifyMsg(null);
    const res = await sendCode(email);
    if (res.ok) setDemoCode(res.demoCode || '');
    else setVerifyMsg(res.message || 'Could not send code.');
    setSending(false);
  };
  const onVerify = async () => {
    setVerifying(true); setVerifyMsg(null);
    const ok = await verifyCode(email, code);
    if (ok) setVerified(true);
    else setVerifyMsg('That code is invalid or expired.');
    setVerifying(false);
  };

  const next = () => {
    if (step === 0) {
      if (!name.trim()) return setError('Please enter your name.');
      if (!EMAIL_RE.test(email.trim())) return setError('Please enter a valid work email.');
      if (password.length < 6) return setError('Choose a password of at least 6 characters.');
    }
    if (step === 1 && !verified) return setError('Please verify your email to continue.');
    if (step === 2 && apps.length === 0) return setError('Pick at least one app to preview.');
    setError(null);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setError(null); setStep(s => Math.max(s - 1, 0)); };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await submitRequest({ name, email, company, role, phone, orgSize, industry, useCase, requestedApps: apps, password });
      setDone(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your request.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <LandingShell>
        <Narrow>
          <Panel style={{ textAlign: 'center', marginTop: 20 }}>
            <Badge><FiClock /></Badge>
            <H2>Request submitted</H2>
            <Sub>
              Thanks{name ? `, ${name.split(' ')[0]}` : ''}. Your request is <b>pending team review</b>.
              We’ll email <b>{email}</b> once you’re approved — then sign in to preview your apps.
            </Sub>
            <CtaRow>
              <PrimaryLink to="/signin">Check status / sign in <FiArrowRight /></PrimaryLink>
              <GhostButton type="button" onClick={() => window.location.assign('/products')}>Back to home</GhostButton>
            </CtaRow>
          </Panel>
        </Narrow>
      </LandingShell>
    );
  }

  return (
    <LandingShell>
      <Narrow>
        <Header><Eyebrow>Preview access</Eyebrow><H2>Request access to ServiceBuds</H2></Header>

        <Stepper>
          {STEPS.map((label, i) => (
            <Node key={label} $state={i < step ? 'done' : i === step ? 'active' : 'todo'}>
              <Dot>{i < step ? <FiCheck /> : i + 1}</Dot>
              <NodeLabel>{label}</NodeLabel>
            </Node>
          ))}
        </Stepper>

        <Panel>
          {step === 0 && (
            <Grid>
              <Field><span className="lbl">Full name</span>
                <input value={name} placeholder="Jane Doe" onChange={e => setName(e.target.value)} /></Field>
              <Field><span className="lbl">Work email</span>
                <input type="email" value={email} placeholder="you@company.com" onChange={e => onEmail(e.target.value)} /></Field>
              <Field><span className="lbl">Create a password</span>
                <input type="password" value={password} placeholder="At least 6 characters" onChange={e => setPassword(e.target.value)} /></Field>
              <Field><span className="lbl">Phone <em>(optional)</em></span>
                <input value={phone} placeholder="+1 555 123 4567" onChange={e => setPhone(e.target.value)} /></Field>
              <Field><span className="lbl">Company <em>(optional)</em></span>
                <input value={company} placeholder="Company Inc." onChange={e => setCompany(e.target.value)} /></Field>
              <Field><span className="lbl">Your role <em>(optional)</em></span>
                <input value={role} placeholder="Operations manager" onChange={e => setRole(e.target.value)} /></Field>
              <Field><span className="lbl">Organization size <em>(optional)</em></span>
                <select value={orgSize} onChange={e => setOrgSize(e.target.value)}>
                  <option value="">Select…</option>
                  {ORG_SIZES.map(o => <option key={o} value={o}>{o} people</option>)}
                </select></Field>
              <Field><span className="lbl">Industry <em>(optional)</em></span>
                <select value={industry} onChange={e => setIndustry(e.target.value)}>
                  <option value="">Select…</option>
                  {INDUSTRIES.map(o => <option key={o} value={o}>{o}</option>)}
                </select></Field>
            </Grid>
          )}

          {step === 1 && (
            <VerifyBox>
              <VerifyIcon><FiMail /></VerifyIcon>
              <p className="lead">Confirm your email so the team can reach you about your request.</p>
              {verified ? (
                <VerifiedBox><FiCheck /> <b>{email}</b> verified</VerifiedBox>
              ) : !demoCode ? (
                <PrimaryButton type="button" onClick={onSendCode} disabled={sending} style={{ margin: '0 auto' }}>
                  {sending ? 'Sending…' : <>Send code to {email}</>}
                </PrimaryButton>
              ) : (
                <>
                  <DemoHint>Demo mode — your code is <code>{demoCode}</code>. In production this is emailed, not shown.</DemoHint>
                  <Field style={{ maxWidth: 220, margin: '0 auto' }}><span className="lbl">Enter 6-digit code</span>
                    <input value={code} maxLength={6} placeholder="••••••" onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                      style={{ textAlign: 'center', letterSpacing: '.3em', fontSize: 18 }} /></Field>
                  {verifyMsg && <div style={{ marginTop: 10 }}><ErrorText role="alert">{verifyMsg}</ErrorText></div>}
                  <Row>
                    <PrimaryButton type="button" onClick={onVerify} disabled={verifying || code.length < 6}>
                      {verifying ? 'Verifying…' : 'Verify email'}
                    </PrimaryButton>
                    <GhostButton type="button" onClick={onSendCode} disabled={sending}>Resend</GhostButton>
                  </Row>
                </>
              )}
            </VerifyBox>
          )}

          {step === 2 && (
            <>
              <FieldHint>Select the apps you’d like to preview. The team approves per app.</FieldHint>
              <AppList>
                {DEPLOYED_APPS.map(app => {
                  const on = apps.includes(app.id);
                  return (
                    <AppRow key={app.id} type="button" $on={on} $accent={app.accent} onClick={() => toggleApp(app.id)}>
                      <Check $on={on} $accent={app.accent}>{on && <FiCheck />}</Check>
                      <div><RowName>{app.name}</RowName><RowSub>{app.tagline}</RowSub></div>
                    </AppRow>
                  );
                })}
              </AppList>
            </>
          )}

          {step === 3 && (
            <Field><span className="lbl">What do you want to try? <em>(optional)</em></span>
              <textarea value={useCase} placeholder="Tell us a bit about your use case…" onChange={e => setUseCase(e.target.value)} /></Field>
          )}

          {step === 4 && (
            <Review>
              <ReviewRow><dt>Name</dt><dd>{name || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Email</dt><dd>{email} <Verified><FiCheck /> verified</Verified></dd></ReviewRow>
              <ReviewRow><dt>Phone</dt><dd>{phone || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Company</dt><dd>{company || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Role</dt><dd>{role || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Org size</dt><dd>{orgSize || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Industry</dt><dd>{industry || '—'}</dd></ReviewRow>
              <ReviewRow><dt>Apps</dt><dd>{apps.map(id => DEPLOYED_APPS.find(a => a.id === id)?.name).filter(Boolean).join(', ')}</dd></ReviewRow>
              <ReviewRow><dt>Use case</dt><dd>{useCase || '—'}</dd></ReviewRow>
              <Note>Submitting sends this to the team for review — access is granted only after approval.</Note>
            </Review>
          )}

          {error && <div style={{ marginTop: 14 }}><ErrorText role="alert">{error}</ErrorText></div>}

          <Actions>
            {step > 0
              ? <GhostButton type="button" onClick={back}><FiArrowLeft /> Back</GhostButton>
              : <PrimaryGhostLink to="/products"><FiArrowLeft /> Cancel</PrimaryGhostLink>}
            {step < STEPS.length - 1
              ? <PrimaryButton type="button" onClick={next}>Continue <FiArrowRight /></PrimaryButton>
              : <PrimaryButton type="button" onClick={submit} disabled={submitting}>
                  {submitting ? 'Submitting…' : <>Submit request <FiCheck /></>}
                </PrimaryButton>}
          </Actions>
        </Panel>
      </Narrow>
    </LandingShell>
  );
};

export default SignupWizard;

// ── styled ────────────────────────────────────────────────────────────────────
const Header = styled.div`margin: 16px 0 18px;`;
const Stepper = styled.div`display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap;`;
interface NodeProps { $state: 'todo' | 'active' | 'done'; }
const Node = styled.div<NodeProps>`display: flex; align-items: center; gap: 8px; flex: 1; min-width: 110px;
  opacity: ${p => p.$state === 'todo' ? .55 : 1};`;
const Dot = styled.span`width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 12.5px; font-weight: 800; color: #fff; background: ${T.accent};`;
const NodeLabel = styled.span`font-size: 12px; font-weight: 700; color: ${T.text};`;

const Grid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  @media (max-width: 560px){ grid-template-columns: 1fr; }
  em { font-weight: 500; font-style: normal; color: ${T.muted}; }`;

const FieldHint = styled.p`font-size: 13.5px; color: ${T.text2}; margin: 0 0 12px;`;

const VerifyBox = styled.div`text-align: center; display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 8px 0;
  .lead { font-size: 14px; color: ${T.text2}; margin: 0; max-width: 400px; }`;
const VerifyIcon = styled.div`width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 24px; color: #fff; background: linear-gradient(135deg, ${T.accent}, ${T.accentDark});`;
const DemoHint = styled.div`font-size: 12.5px; color: #9A6510; background: ${T.warnBg}; border-radius: 10px; padding: 10px 14px;
  code { font-weight: 800; font-size: 15px; letter-spacing: .1em; }`;
const VerifiedBox = styled.div`display: inline-flex; align-items: center; gap: 8px; font-size: 14.5px; font-weight: 700; color: #1B7A52;
  background: #E3F3EC; border-radius: 12px; padding: 12px 18px; svg { font-size: 17px; }`;
const Row = styled.div`display: flex; gap: 10px; justify-content: center;`;
const Verified = styled.span`display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 700; color: #1B7A52;
  background: #E3F3EC; border-radius: 20px; padding: 2px 8px; margin-left: 6px;`;

const AppList = styled.div`display: flex; flex-direction: column; gap: 10px;`;
interface RowProps { $on: boolean; $accent: string; }
const AppRow = styled.button<RowProps>`display: flex; align-items: center; gap: 12px; text-align: left; cursor: pointer;
  padding: 14px; border-radius: 13px; font-family: inherit;
  background: ${p => p.$on ? '#F1F8F6' : T.field}; border: 1.5px solid ${p => p.$on ? p.$accent : T.fieldBorder};
  transition: border-color .14s ease, background .14s ease;`;
const Check = styled.span<RowProps>`width: 24px; height: 24px; flex-shrink: 0; border-radius: 7px; display: flex; align-items: center;
  justify-content: center; color: #fff; font-size: 14px; background: ${p => p.$on ? p.$accent : '#fff'};
  border: 1.5px solid ${p => p.$on ? p.$accent : '#cbd5d3'};`;
const RowName = styled.div`font-size: 14.5px; font-weight: 700; color: ${T.text};`;
const RowSub = styled.div`font-size: 12.5px; color: ${T.muted};`;

const Review = styled.dl`margin: 0; display: flex; flex-direction: column; gap: 2px;`;
const ReviewRow = styled.div`display: grid; grid-template-columns: 110px 1fr; gap: 10px; padding: 9px 0; border-bottom: 1px solid ${T.field};
  dt { font-size: 12.5px; font-weight: 700; color: ${T.muted}; margin: 0; } dd { font-size: 14px; color: ${T.text}; margin: 0; }`;
const Note = styled.p`font-size: 12.5px; color: ${T.muted}; margin: 14px 0 0;`;

const Actions = styled.div`display: flex; justify-content: space-between; gap: 12px; margin-top: 22px;`;
const PrimaryGhostLink = styled(Link)`display: inline-flex; align-items: center; gap: 8px; padding: 13px 20px; border-radius: 13px;
  text-decoration: none; font-size: 15px; font-weight: 700; color: ${T.text2}; background: ${T.field}; &:hover { background: #E9EEEE; }`;

const Badge = styled.div`width: 58px; height: 58px; border-radius: 50%; margin: 0 auto 8px; display: flex; align-items: center;
  justify-content: center; font-size: 26px; color: #fff; background: linear-gradient(135deg, ${T.warn}, #9A6510);`;
const CtaRow = styled.div`display: flex; gap: 12px; justify-content: center; margin-top: 18px; flex-wrap: wrap;`;
